'use client';

import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';

import { HeaderCartButton } from '@eliadmin/core/src/components/header/header-cart-button';
import { HeaderFavoritesButton } from '@eliadmin/core/src/components/header/header-favorites-button';
import { HeaderLogo } from '@eliadmin/core/src/components/header/header-logo';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import {
  MobileSearchBarContainer,
  MobileSearchProvider,
} from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useEffect, useState } from 'react';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  templateSettings: TemplateSettings['data'];
  categoriesTemporary: CategoriesEntity[];
  menu: Menu[];
  className?: {
    root?: string;
    container?: string;
    toolsNavigation?: string;
    logo?: string;
    mobileMenuButton?: string;
    searchIcon?: string;
    search?: string;
    headerCategoriesButton?: string;
    headerMenu?: string;
    headerToolIcons?: string;
    profile?: string;
    contact?: string;
    cart?: string;
    socialIcons?: string;
    customHeader?: string;
    language?: string;
  };
};

export function HeaderMain({
  templateSettings,
  generalLayout,
  className,
  menu,
  categoriesTemporary,
}: HeaderMainProps) {
  const me = useCustomerStore(s => s.me);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'fixed left-0 top-0 z-[54] w-full bg-white py-4 sm:py-6 lg:py-8',
          'ea-header-main',
          scrolled && 'ea-header-main__scrolled',
        )}
        style={{ order: templateSettings.header?.tools_navigation?.order }}
      >
        <div className={cn('custom-section', className?.container)}>
          {/* mobile */}
          <div
            className={cn(
              'grid w-full grid-cols-12 items-center py-2 sm:hidden',
              'ea-header-main__mobile-header',
            )}
          >
            {!!templateSettings.header?.mobile_classic_menu?.enabled && (
              <div
                className={cn(
                  'col-span-2 flex items-center justify-start',
                  'ea-header-main__mobile-menu-button',
                  className?.mobileMenuButton,
                )}
              >
                <MobileSidebar
                  side="left"
                  menu={menu[0] && menu[0].fields}
                  menuArrow
                  menuSeparator
                  categories={categoriesTemporary}
                />
              </div>
            )}

            {/* if there is a search icon center logo  */}
            {!!templateSettings.header?.logo?.order && (
              <div
                className={cn(
                  'flex justify-center p-0',
                  {
                    'col-span-8 text-center': !!templateSettings.header?.search_icon?.enabled,
                    'col-span-10 text-right': !templateSettings.header?.search_icon?.enabled,
                  },
                  'ea-header-main__logo',
                )}
                style={{ order: templateSettings.header?.logo.order }}
              >
                <HeaderLogo generalLayout={generalLayout} />
              </div>
            )}

            {!!templateSettings.header?.search_icon?.enabled && (
              <div
                className={cn(
                  'col-span-2 flex justify-end',
                  'ea-header-main__search-icon',
                  className?.searchIcon,
                )}
                style={{ order: templateSettings.header?.search_icon.order }}
              >
                <SearchButton variant="ghost" />
              </div>
            )}
            {!!templateSettings.header?.search?.enabled && (
              // force MobileSearchBarContainer to be last
              <div className={cn('col-span-12  w-full')} style={{ order: 999 }}>
                <MobileSearchBarContainer className="py-2" />
              </div>
            )}
            {/* avalability check */}
          </div>

          {/* desktop */}
          <div
            className={cn(
              'custom-container hidden items-center justify-between sm:flex',
              'ea-header-main__tools-navigation',
              className?.toolsNavigation,
            )}
          >
            <MobileSidebar categories={categoriesTemporary} />
            <HeaderLogo generalLayout={generalLayout} />
            <div
              className={cn('flex items-center justify-end gap-8', 'ea-header-main__tools-wrapper')}
            >
              <SearchButton />
              <HeaderCartButton />
              {me && <HeaderFavoritesButton />}

              <HeaderProfileButton />
            </div>
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}

